/* eslint-disable react-refresh/only-export-components */
import * as React from 'react';

export type MemberBrand = { id: string; name: string };

export type GrantingRoles = { roleNames: readonly string[]; roleCount: number };

export type AccessAdmin = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
};

type PermissionsContextValue = {
  permissions: string[];
  hasActiveBrandMembership: boolean;
  loading: boolean;
  // WHY: optional so existing tests stubbing usePermissions with a bare {permissions, loading} literal still compile; the real provider always supplies them.
  hasMultiStateAccess?: boolean;
  memberBrands?: MemberBrand[];
  /** `memberBrands` plus any since-deactivated brand the user still belongs to, whose history stays readable. */
  readableBrands?: MemberBrand[];
  /** Undefined until the access directory loads; the denied page and tooltips then name roles. */
  roleNames?: readonly string[];
  /** Keyed by a permission the caller lacks. */
  grantingRoles?: ReadonlyMap<string, GrantingRoles>;
  accessAdmins?: readonly AccessAdmin[];
};

const PermissionsContext = React.createContext<PermissionsContextValue>({
  permissions: [],
  hasActiveBrandMembership: false,
  memberBrands: [],
  readableBrands: [],
  loading: true,
  hasMultiStateAccess: false,
});

const NO_MEMBER_BRANDS: MemberBrand[] = [];

export function PermissionsProvider({
  permissions,
  hasActiveBrandMembership = false,
  memberBrands = NO_MEMBER_BRANDS,
  readableBrands = memberBrands,
  loading,
  hasMultiStateAccess = false,
  roleNames,
  grantingRoles,
  accessAdmins,
  children,
}: Readonly<{
  permissions: string[];
  hasActiveBrandMembership?: boolean;
  memberBrands?: MemberBrand[];
  readableBrands?: MemberBrand[];
  loading: boolean;
  hasMultiStateAccess?: boolean;
  roleNames?: readonly string[];
  grantingRoles?: ReadonlyMap<string, GrantingRoles>;
  accessAdmins?: readonly AccessAdmin[];
  children: React.ReactNode;
}>) {
  const value = React.useMemo(
    () => ({
      permissions,
      hasActiveBrandMembership,
      memberBrands,
      readableBrands,
      loading,
      hasMultiStateAccess,
      roleNames,
      grantingRoles,
      accessAdmins,
    }),
    [
      permissions,
      hasActiveBrandMembership,
      memberBrands,
      readableBrands,
      loading,
      hasMultiStateAccess,
      roleNames,
      grantingRoles,
      accessAdmins,
    ],
  );
  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
}

export function usePermissions(): PermissionsContextValue {
  return React.useContext(PermissionsContext);
}
